import { apiService } from './apiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface WeeklyRecord {
  id: number;
  energy: number;
  sleep: number;
  digestion: number;
  comments: string;
  recordDate: string;
  averageScore: number;
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyRecordsResponse {
  message: string;
  patient: {
    id: number;
    name: string;
    email: string;
  };
  data: WeeklyRecord[];
  count: number;
  filters: {
    start_date: string | null;
    end_date: string | null;
  };
}

export interface WeeklyRecordsFilters {
  start_date?: string;
  end_date?: string;
}

// Helper function to get date 10 weeks ago
const getTenWeeksAgo = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - (10 * 7)); // 10 weeks * 7 days
  return formatDateForAPI(date);
};

// Helper function to get current date
const getCurrentDate = (): string => {
  return formatDateForAPI(new Date());
};

// Helper function to format date as DD-MM-YYYY for the API
const formatDateForAPI = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const weeklyRecordsService = {
  /**
   * Get weekly records for a patient
   * @param patientId - The patient ID to get records for
   * @param filters - Optional filters for date range
   * @returns Promise<WeeklyRecordsResponse>
   */
  getWeeklyRecords: async (
    patientId: string | number, 
    filters?: WeeklyRecordsFilters
  ): Promise<WeeklyRecordsResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add date filters if provided
    if (filters?.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      params.append('end_date', filters.end_date);
    }

    const queryString = params.toString();
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/weekly-records/${queryString ? `?${queryString}` : ''}`;
    
    return await apiService.get(url);
  },

  /**
   * Get weekly records for the last 10 weeks
   * @param patientId - The patient ID to get records for
   * @returns Promise<WeeklyRecord[]>
   */
  getLastTenWeeksRecords: async (patientId: string | number): Promise<WeeklyRecord[]> => {
    const response = await weeklyRecordsService.getWeeklyRecords(patientId, {
      start_date: getTenWeeksAgo(),
      end_date: getCurrentDate()
    });
    
    // Return the data array, sorted by date (newest first)
    return response.data.sort((a, b) => 
      new Date(b.recordDate).getTime() - new Date(a.recordDate).getTime()
    );
  }
};
